// v10/src/components/ChecklistItem.js
import React, { memo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertTriangle, Clock, Eye, Camera, ChevronDown } from 'lucide-react';

const ChecklistItem = memo(({
  itemId,
  item,
  isChecked,
  isSkipped,
  isPending,
  isCurrent,
  onCheck,
  index,
  reviewingSkipped,
  onShowReference,
  hasReferenceImage,
  photoGallery,
  darkMode,
  // ✅ NUEVO: foto obligatoria
  requiresPhoto,       // bool — este ítem exige al menos 1 foto
  hasPhoto,            // bool — ya tiene al menos 1 foto adjunta
  onRequestPhoto,      // fn  — abre el panel de fotos cuando se intenta chequear sin foto
  onHobbsConfirm,      // fn  — llamado con el valor numérico cuando item_type === 'hobbs'
  onOilConfirm,        // fn  — llamado con el valor numérico cuando item_type === 'oil'
  onFuelConfirm,       // fn  — llamado con { left, right } cuando item_type === 'fuel'
  aircraft,            // obj — aeronave seleccionada (para single_tank, capacidades, unit)
}) => {
  const [showPhotos, setShowPhotos]         = useState(false);
  const [showPhotoWarn, setShowPhotoWarn]   = useState(false);
  const [hobbsValue, setHobbsValue]         = useState('');
  const [hobbsError, setHobbsError]         = useState('');
  const [oilValue, setOilValue]             = useState('');
  const [oilError, setOilError]             = useState('');
  const [fuelLeftValue, setFuelLeftValue]   = useState('');
  const [fuelRightValue, setFuelRightValue] = useState('');
  const [fuelError, setFuelError]           = useState('');

  const itemText   = typeof item === 'string' ? item : item.text;
  const itemDesc   = typeof item === 'object' ? item.description : null;
  const isHobbs    = typeof item === 'object' && item.item_type === 'hobbs';
  const isOil      = typeof item === 'object' && item.item_type === 'oil';
  const isFuel     = typeof item === 'object' && item.item_type === 'fuel';

  const singleTank   = aircraft?.single_tank === true;
  const fuelSide     = singleTank ? 'left' : (item?.fuel_side || 'both');
  const showLeft     = isFuel && (fuelSide === 'left' || fuelSide === 'both');
  const showRight    = isFuel && !singleTank && (fuelSide === 'right' || fuelSide === 'both');
  const fuelUnit     = aircraft?.fuel_unit || 'L';
  const capLeft      = aircraft?.fuel_capacity_left  != null ? parseFloat(aircraft.fuel_capacity_left)  : null;
  const capRight     = aircraft?.fuel_capacity_right != null ? parseFloat(aircraft.fuel_capacity_right) : null;

  const handleHobbsConfirm = () => {
    const val = parseFloat(hobbsValue);
    if (!hobbsValue || isNaN(val) || val <= 0) {
      setHobbsError('Ingresá un valor válido mayor a 0');
      return;
    }
    setHobbsError('');
    onHobbsConfirm?.(val);
    onCheck();
  };

  const handleOilConfirm = () => {
    const val = parseFloat(oilValue);
    if (!oilValue || isNaN(val) || val <= 0 || val >= 99) {
      setOilError('Ingresá un valor entre 0 y 99 qt');
      return;
    }
    setOilError('');
    onOilConfirm?.(val);
    onCheck();
  };

  const handleFuelConfirm = () => {
    const left  = showLeft  && fuelLeftValue  !== '' ? parseFloat(fuelLeftValue)  : null;
    const right = showRight && fuelRightValue !== '' ? parseFloat(fuelRightValue) : null;
    if ((showLeft && left == null) || (showRight && right == null)) {
      setFuelError('Completá el valor del combustible');
      return;
    }
    if (left != null && (isNaN(left) || left < 0)) {
      setFuelError('Valor inválido para el tanque izquierdo');
      return;
    }
    if (right != null && (isNaN(right) || right < 0)) {
      setFuelError('Valor inválido para el tanque derecho');
      return;
    }
    if (left != null && capLeft != null && left > capLeft) {
      setFuelError(`Excede la capacidad del tanque izquierdo (${capLeft} ${fuelUnit})`);
      return;
    }
    if (right != null && capRight != null && right > capRight) {
      setFuelError(`Excede la capacidad del tanque derecho (${capRight} ${fuelUnit})`);
      return;
    }
    setFuelError('');
    onFuelConfirm?.({ left, right });
    onCheck();
  };

  // ¿Está bloqueado por falta de foto?
  const blockedByPhoto = requiresPhoto && !hasPhoto && !isChecked;

  // ── Estado visual ─────────────────────────────────────────────
  const state = isChecked ? 'checked'
    : isSkipped   ? 'skipped'
    : isCurrent   ? 'current'
    : 'idle';

  const stateStyles = {
    checked: {
      wrap:  darkMode ? 'bg-gray-800 border-green-700/50' : 'bg-green-50/80 border-green-200',
      dot:   'bg-green-500',
      text:  darkMode ? 'text-gray-300 line-through' : 'text-gray-400 line-through',
      badge: darkMode ? 'text-green-400' : 'text-green-600',
      label: '✓ Verificado',
    },
    skipped: {
      wrap:  darkMode ? 'bg-yellow-900/40 border-yellow-600/60' : 'bg-yellow-50 border-yellow-200',
      dot:   'bg-yellow-400',
      text:  darkMode ? 'text-yellow-200 line-through' : 'text-yellow-700/70 line-through',
      badge: darkMode ? 'text-yellow-300' : 'text-yellow-600',
      label: '⚠ Omitido',
    },
    current: {
      wrap:  darkMode
        ? 'bg-blue-900/30 border-blue-500/60 ring-1 ring-blue-500/30'
        : 'bg-blue-50 border-blue-300 ring-1 ring-blue-200',
      dot:   'bg-blue-500 animate-pulse',
      text:  darkMode ? 'text-white font-semibold' : 'text-gray-900 font-semibold',
      badge: darkMode ? 'text-blue-400' : 'text-blue-600',
      label: '● Ítem actual',
    },
    idle: {
      wrap:  darkMode
        ? 'bg-gray-800 border-gray-700 hover:border-gray-500'
        : 'bg-white border-gray-200 hover:border-gray-300',
      dot:   'bg-gray-200 dark:bg-gray-500',
      text:  darkMode ? 'text-white' : 'text-gray-800',
      badge: darkMode ? 'text-gray-500' : 'text-gray-400',
      label: '',
    },
  };

  const s = stateStyles[state];

  // Si item requiere foto, override del borde para destacarlo (solo si no chequeado)
  const wrapExtra = blockedByPhoto
    ? darkMode
      ? ' border-orange-600/60 bg-orange-900/10'
      : ' border-orange-300 bg-orange-50/60'
    : '';

  const handleCheckClick = () => {
    if (blockedByPhoto) {
      // Abre el panel de fotos y muestra aviso
      setShowPhotos(true);
      setShowPhotoWarn(true);
      onRequestPhoto?.();
      setTimeout(() => setShowPhotoWarn(false), 3000);
      return;
    }
    onCheck();
  };

  return (
    <motion.div
      id={itemId}
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: index * 0.03 }}
      className={`rounded-2xl border transition-all duration-200 overflow-hidden ${s.wrap}${wrapExtra}`}
    >
      {/* ── Tira superior: badge "foto requerida" ────────────── */}
      {requiresPhoto && !isChecked && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '5px 14px',
          background: hasPhoto
            ? (darkMode ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)')
            : (darkMode ? 'rgba(249,115,22,0.15)' : 'rgba(249,115,22,0.10)'),
          borderBottom: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
        }}>
          <Camera style={{ width: '11px', height: '11px', flexShrink: 0, color: hasPhoto ? '#22c55e' : '#f97316' }} />
          <span style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
            color: hasPhoto ? '#22c55e' : '#f97316',
            textTransform: 'uppercase',
          }}>
            {hasPhoto ? 'Foto adjunta ✓' : 'Foto obligatoria antes de chequear'}
          </span>
        </div>
      )}

      {/* ── Main row ─────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3.5">

        {/* Check button */}
        <motion.button
          onClick={(isHobbs || isOil || isFuel) && !isChecked ? undefined : handleCheckClick}
          disabled={isChecked || (isSkipped && !reviewingSkipped) || ((isHobbs || isOil || isFuel) && !isChecked)}
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isChecked
              ? 'bg-green-500 text-white'
              : blockedByPhoto
              ? darkMode
                ? 'bg-orange-800/60 border border-orange-600 text-orange-400 cursor-pointer'
                : 'bg-orange-50 border border-orange-300 text-orange-400 cursor-pointer'
              : isSkipped
              ? 'bg-yellow-400 text-white cursor-default'
              : isCurrent
              ? 'bg-blue-500 text-white shadow-sm shadow-blue-200 dark:shadow-blue-900'
              : darkMode
              ? 'bg-gray-700 border border-gray-600 text-gray-400 hover:border-blue-500 hover:text-blue-400'
              : 'bg-gray-50 border border-gray-200 text-gray-300 hover:border-blue-400 hover:text-blue-500'
          }`}
          whileHover={!isChecked && !isSkipped ? { scale: 1.08 } : {}}
          whileTap={{ scale: 0.92 }}
          title={blockedByPhoto ? 'Adjuntá una foto antes de chequear' : undefined}
        >
          {isChecked    && <Check className="w-4 h-4" strokeWidth={3} />}
          {!isChecked && isSkipped && <AlertTriangle className="w-3.5 h-3.5" />}
          {!isChecked && !isSkipped && blockedByPhoto && <Camera className="w-3.5 h-3.5" />}
          {!isChecked && !isSkipped && !blockedByPhoto && isCurrent  && <Clock className="w-3.5 h-3.5" />}
          {!isChecked && !isSkipped && !blockedByPhoto && !isCurrent && (
            <div className={`w-2 h-2 rounded-full ${s.dot}`} />
          )}
        </motion.button>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-snug ${s.text}`}>{itemText}</p>
          {itemDesc && !isChecked && (
            <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {itemDesc}
            </p>
          )}
          {s.label && (
            <p className={`text-xs mt-0.5 font-medium ${s.badge}`}>{s.label}</p>
          )}
        </div>



        {/* Action icons */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasReferenceImage && onShowReference && (
            <motion.button
              onClick={onShowReference}
              className={`p-1.5 rounded-xl transition-all ${
                darkMode ? 'text-blue-300 bg-blue-900/30 hover:bg-blue-900/60' : 'text-blue-500 hover:bg-blue-50'
              }`}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Ver referencia"
            >
              <Eye className="w-4 h-4" />
            </motion.button>
          )}

          {/* Fotos — visible si hay foto requerida, está activo o ya chequeado */}
          {(requiresPhoto || isCurrent || isChecked) && (
            <motion.button
              onClick={() => setShowPhotos(v => !v)}
              className={`p-1.5 rounded-xl transition-all flex items-center gap-1 ${
                showPhotos
                  ? darkMode ? 'bg-purple-900/40 text-purple-400' : 'bg-purple-50 text-purple-500'
                  : requiresPhoto && !hasPhoto && !isChecked
                  ? darkMode ? 'text-orange-400 hover:bg-orange-900/30' : 'text-orange-500 hover:bg-orange-50'
                  : darkMode ? 'text-gray-300 hover:bg-gray-700/60' : 'text-gray-400 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Fotos"
            >
              <Camera className="w-4 h-4" />
              <motion.div animate={{ rotate: showPhotos ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-3 h-3" />
              </motion.div>
            </motion.button>
          )}
        </div>
      </div>

      {/* ── Hobbs input panel — full width debajo del main row ── */}
      {isHobbs && !isChecked && (
        <div style={{
          padding: '12px 16px 14px',
          borderTop: darkMode ? '1px solid rgba(99,102,241,0.15)' : '1px solid rgba(99,102,241,0.12)',
          background: darkMode ? 'rgba(30,40,80,0.4)' : 'rgba(238,242,255,0.5)',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(165,180,252,0.7)' : '#6366f1' }}>
            🕐 Horómetro actual
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="number"
                step="0.1"
                min="0"
                value={hobbsValue}
                onChange={e => { setHobbsValue(e.target.value); setHobbsError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleHobbsConfirm()}
                placeholder="ej: 1259.5"
                onClick={e => e.stopPropagation()}
                style={{
                  width: '100%', padding: '10px 40px 10px 14px', borderRadius: '12px',
                  border: hobbsError
                    ? '1.5px solid #ef4444'
                    : darkMode ? '1.5px solid rgba(99,102,241,0.5)' : '1.5px solid rgba(99,102,241,0.35)',
                  background: darkMode ? 'rgba(20,30,70,0.7)' : 'white',
                  color: darkMode ? 'white' : '#1e293b',
                  fontSize: '16px', fontWeight: 700, fontFamily: 'monospace',
                  outline: 'none', boxSizing: 'border-box',
                  WebkitAppearance: 'none', MozAppearance: 'textfield',
                }}
              />
              <span style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '12px', fontWeight: 700,
                color: darkMode ? 'rgba(165,180,252,0.6)' : '#6366f1',
                pointerEvents: 'none',
              }}>hs</span>
            </div>
            <motion.button
              onClick={e => { e.stopPropagation(); handleHobbsConfirm(); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              style={{
                padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: hobbsValue && !hobbsError
                  ? 'linear-gradient(135deg,#4f46e5,#7c3aed)'
                  : darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: hobbsValue && !hobbsError ? 'white' : darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8',
                fontSize: '14px', fontWeight: 800, transition: 'all 0.2s', flexShrink: 0,
                boxShadow: hobbsValue && !hobbsError ? '0 4px 14px rgba(79,70,229,0.35)' : 'none',
              }}
            >
              Confirmar ✓
            </motion.button>
          </div>
          {hobbsError && (
            <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>
              ⚠ {hobbsError}
            </p>
          )}
        </div>
      )}

      {/* ── Oil input panel — full width debajo del main row ──── */}
      {isOil && !isChecked && (
        <div style={{
          padding: '12px 16px 14px',
          borderTop: darkMode ? '1px solid rgba(99,102,241,0.15)' : '1px solid rgba(99,102,241,0.12)',
          background: darkMode ? 'rgba(30,40,80,0.4)' : 'rgba(238,242,255,0.5)',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(165,180,252,0.7)' : '#6366f1' }}>
            🛢 Nivel de aceite
          </p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="number"
                step="0.1"
                min="0"
                value={oilValue}
                onChange={e => { setOilValue(e.target.value); setOilError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleOilConfirm()}
                placeholder="ej: 7.0"
                onClick={e => e.stopPropagation()}
                style={{
                  width: '100%', padding: '10px 40px 10px 14px', borderRadius: '12px',
                  border: oilError
                    ? '1.5px solid #ef4444'
                    : darkMode ? '1.5px solid rgba(99,102,241,0.5)' : '1.5px solid rgba(99,102,241,0.35)',
                  background: darkMode ? 'rgba(20,30,70,0.7)' : 'white',
                  color: darkMode ? 'white' : '#1e293b',
                  fontSize: '16px', fontWeight: 700, fontFamily: 'monospace',
                  outline: 'none', boxSizing: 'border-box',
                  WebkitAppearance: 'none', MozAppearance: 'textfield',
                }}
              />
              <span style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                fontSize: '12px', fontWeight: 700,
                color: darkMode ? 'rgba(165,180,252,0.6)' : '#6366f1',
                pointerEvents: 'none',
              }}>qt</span>
            </div>
            <motion.button
              onClick={e => { e.stopPropagation(); handleOilConfirm(); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              style={{
                padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: oilValue && !oilError
                  ? 'linear-gradient(135deg,#4f46e5,#7c3aed)'
                  : darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: oilValue && !oilError ? 'white' : darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8',
                fontSize: '14px', fontWeight: 800, transition: 'all 0.2s', flexShrink: 0,
                boxShadow: oilValue && !oilError ? '0 4px 14px rgba(79,70,229,0.35)' : 'none',
              }}
            >
              Confirmar ✓
            </motion.button>
          </div>
          {oilError && (
            <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>
              ⚠ {oilError}
            </p>
          )}
        </div>
      )}

      {/* ── Fuel input panel — 1 o 2 tanques según single_tank/fuel_side ── */}
      {isFuel && !isChecked && (
        <div style={{
          padding: '12px 16px 14px',
          borderTop: darkMode ? '1px solid rgba(16,185,129,0.15)' : '1px solid rgba(16,185,129,0.12)',
          background: darkMode ? 'rgba(6,40,30,0.4)' : 'rgba(236,253,245,0.6)',
        }}>
          <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: darkMode ? 'rgba(110,231,183,0.85)' : '#047857' }}>
            ⛽ Combustible de salida
            {singleTank ? ' · 1 tanque' : fuelSide === 'both' ? ' · izquierdo + derecho' : fuelSide === 'left' ? ' · izquierdo' : ' · derecho'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {showLeft && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="number" step="0.1" min="0"
                    value={fuelLeftValue}
                    onChange={e => { setFuelLeftValue(e.target.value); setFuelError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleFuelConfirm()}
                    placeholder={singleTank ? `ej: ${capLeft != null ? capLeft : 80}` : `Izq · ej: ${capLeft != null ? capLeft : 40}`}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: '100%', padding: '10px 56px 10px 14px', borderRadius: '12px',
                      border: fuelError
                        ? '1.5px solid #ef4444'
                        : darkMode ? '1.5px solid rgba(16,185,129,0.45)' : '1.5px solid rgba(16,185,129,0.35)',
                      background: darkMode ? 'rgba(8,30,22,0.7)' : 'white',
                      color: darkMode ? 'white' : '#064e3b',
                      fontSize: '16px', fontWeight: 700, fontFamily: 'monospace',
                      outline: 'none', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'textfield',
                    }}
                  />
                  <span style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '12px', fontWeight: 700,
                    color: darkMode ? 'rgba(110,231,183,0.7)' : '#047857',
                    pointerEvents: 'none',
                  }}>{singleTank ? fuelUnit : `${fuelUnit} izq`}</span>
                </div>
              </div>
            )}
            {showRight && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="number" step="0.1" min="0"
                    value={fuelRightValue}
                    onChange={e => { setFuelRightValue(e.target.value); setFuelError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleFuelConfirm()}
                    placeholder={`Der · ej: ${capRight != null ? capRight : 40}`}
                    onClick={e => e.stopPropagation()}
                    style={{
                      width: '100%', padding: '10px 56px 10px 14px', borderRadius: '12px',
                      border: fuelError
                        ? '1.5px solid #ef4444'
                        : darkMode ? '1.5px solid rgba(16,185,129,0.45)' : '1.5px solid rgba(16,185,129,0.35)',
                      background: darkMode ? 'rgba(8,30,22,0.7)' : 'white',
                      color: darkMode ? 'white' : '#064e3b',
                      fontSize: '16px', fontWeight: 700, fontFamily: 'monospace',
                      outline: 'none', boxSizing: 'border-box',
                      WebkitAppearance: 'none', MozAppearance: 'textfield',
                    }}
                  />
                  <span style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '12px', fontWeight: 700,
                    color: darkMode ? 'rgba(110,231,183,0.7)' : '#047857',
                    pointerEvents: 'none',
                  }}>{fuelUnit} der</span>
                </div>
              </div>
            )}
            <motion.button
              onClick={e => { e.stopPropagation(); handleFuelConfirm(); }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              style={{
                padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer',
                background: (showLeft ? fuelLeftValue : true) && (showRight ? fuelRightValue : true) && !fuelError
                  ? 'linear-gradient(135deg,#10b981,#047857)'
                  : darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: (showLeft ? fuelLeftValue : true) && (showRight ? fuelRightValue : true) && !fuelError ? 'white' : darkMode ? 'rgba(255,255,255,0.25)' : '#94a3b8',
                fontSize: '14px', fontWeight: 800, transition: 'all 0.2s',
                boxShadow: (showLeft ? fuelLeftValue : true) && (showRight ? fuelRightValue : true) && !fuelError ? '0 4px 14px rgba(16,185,129,0.35)' : 'none',
              }}
            >
              Confirmar ✓
            </motion.button>
          </div>
          {fuelError && (
            <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#ef4444', fontWeight: 600 }}>
              ⚠ {fuelError}
            </p>
          )}
        </div>
      )}

      {/* ── Aviso inline cuando intenta chequear sin foto ──────── */}
      <AnimatePresence>
        {showPhotoWarn && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              padding: '8px 16px',
              background: darkMode ? 'rgba(249,115,22,0.15)' : 'rgba(249,115,22,0.10)',
              borderTop: darkMode ? '1px solid rgba(249,115,22,0.2)' : '1px solid rgba(249,115,22,0.15)',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <span style={{ fontSize: '14px' }}>📸</span>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: darkMode ? '#fb923c' : '#ea580c' }}>
              Adjuntá al menos una foto para poder chequear este ítem.
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Photos panel (expandible) ─────────────────────────── */}
      <AnimatePresence>
        {showPhotos && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={`border-t px-4 pb-4 pt-3 ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}
          >
            {photoGallery}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

ChecklistItem.displayName = 'ChecklistItem';
export default ChecklistItem;
